import { IonIcon, IonItem, IonToast, ToastButton } from '@ionic/react';
import { alertOutline, closeOutline } from 'ionicons/icons';
import {
  MutableRefObject,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { FlowValidateError, RuleResponseLevel } from '../../model/flow.types';

export enum FlowErrorToastStatus {
  DANGER = 'danger',
  INFO = 'light',
  WARN = 'warning',
}

interface FlowErrorsProps {
  flowValidationErrors: FlowValidateError[];
  status?: FlowErrorToastStatus;
  jumpToItem: (itemId: string) => void;
}

export const FlowErrors = (props: FlowErrorsProps): ReactElement => {
  const { flowValidationErrors, status } = props;
  const toastRef: MutableRefObject<HTMLIonToastElement | null> = useRef(null);
  const open = flowValidationErrors.length > 0;
  const itemId = flowValidationErrors[0]?.startItemId;
  const message = flowValidationErrors[0]?.message;

  const { jumpToItem } = props;

  const buttons: ToastButton[] = useMemo(
    () => [
      {
        side: 'end',
        text: getLocalizedText('common:jump_to_error'),
        handler: () => {
          if (itemId) {
            jumpToItem(itemId);
          }
          return false;
        },
      },
    ],
    [itemId, jumpToItem]
  );

  useEffect(() => {
    // since we persist the toast when there is an error message we might change
    // the message while it is still presented. we force this change by using the
    // the toast ref since the prop changing doesn't trigger it to redraw. we could
    // use the key prop on the toast but then it will flash as the message changes
    if (toastRef.current) {
      toastRef.current.message = message;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toastRef.current.buttons = buttons as any;
      toastRef.current.color = status || 'danger';
    }
  }, [message, status, buttons]);

  const color = status || 'danger';

  return (
    <IonToast
      ref={toastRef}
      isOpen={open}
      color={color}
      position={'top'}
      message={message}
      buttons={buttons}
    />
  );
};

interface FlowErrorProps {
  error: FlowValidateError;
}

export const FlowError = (props: FlowErrorProps): ReactElement => {
  const { level, message } = props.error;

  const icon = (level: RuleResponseLevel): string => {
    switch (level) {
      case RuleResponseLevel.ERROR:
        return closeOutline;
      case RuleResponseLevel.WARN:
        return alertOutline;
      default:
        return RuleResponseLevel.ERROR;
    }
  };

  return (
    <IonItem>
      <IonIcon icon={icon(level)} />
      {message}
    </IonItem>
  );
};
